import React, { useEffect, forwardRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import AutoplayPlugin from 'embla-carousel-autoplay';
import ClassNamesPlugin from 'embla-carousel-class-names';
import Arrows from './components/Arrows';
import Counter from './components/Counter';
import Dots from './components/Dots';
import Autoplay from './components/Autoplay';
import { transformConfig, getCarouselState, addSlideInViewAtrribute } from './utils';
import {
  useHandleGapInLoop, useCounter, useDots, useArrows, useAutoplay,
} from './hooks';

import config from './configs';
import './Carousel.scss';

function Carousel(props, ref) {
  const {
    options = {}, onSettle, onSelect, onSlidesInView, children, dataAttributes, onCarouselApiReady, className = '',
  } = props;
  const carouselConfig = transformConfig({ ...config, ...options });
  const {
    delay, playOnInit, stopOnInteraction, stopOnFocusIn, stopOnMouseEnter,
  } = carouselConfig;

  const plugins = [];
  plugins.push(ClassNamesPlugin());
  playOnInit && plugins.push(AutoplayPlugin({
    playOnInit, delay, stopOnInteraction, stopOnFocusIn, stopOnMouseEnter,
  }));

  const [emblaRef, emblaApi] = useEmblaCarousel(carouselConfig, plugins);

  useHandleGapInLoop(emblaApi, ref);
  const counterHook = useCounter(emblaApi);
  const dotsHook = useDots(emblaApi);
  const arrowsHook = useArrows(emblaApi);
  const autoplayHook = useAutoplay(emblaApi);

  const carouselState = getCarouselState(emblaApi);

  const { isInverted, isClipped, loop } = carouselState;

  const onSettleCallback = (event) => {
    onSettle?.(event);
  };

  const onSelectCallback = (event) => {
    onSelect?.(event);
  };

  const onSlidesInViewCallback = () => {
    addSlideInViewAtrribute(emblaApi);
    onSlidesInView?.();
  };

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('settle', onSettleCallback);
      emblaApi.on('select', onSelectCallback);
      emblaApi.on('slidesInView', onSlidesInViewCallback);
    }
  }, [emblaApi]);

  useEffect(() => {
    if (emblaApi && onCarouselApiReady) {
      onCarouselApiReady(emblaApi);
    }
  }, [emblaApi]);

  const getCarouselClassNames = () => {
    const classNames = [
      'mn_carousel',
      className,
      isInverted ? 'mn_inverted' : '',
      isClipped ? 'mn_clipped' : '',
      loop ? 'mn_loop' : '',
    ];

    return classNames.filter(Boolean).join(' ');
  };

  const carouselClassNames = getCarouselClassNames();

  return (
    <div className={carouselClassNames}>
      <div className="mn_carouselViewportWithNav">
        <div className="mn_carouselViewport" ref={emblaRef}>
          <div className="mn_list" ref={ref} {...dataAttributes}>
            {children}
          </div>
        </div>
        <Arrows emblaApi={emblaApi} arrowsHook={arrowsHook} carouselState={carouselState} />
      </div>
      <div className="mn_carouselNavigation">
        <Counter emblaApi={emblaApi} counterHook={counterHook} arrowsHook={arrowsHook} carouselState={carouselState} />
        <Dots emblaApi={emblaApi} dotsHook={dotsHook} carouselState={carouselState} />
        <Autoplay emblaApi={emblaApi} autoplayHook={autoplayHook} carouselState={carouselState} />
      </div>
    </div>
  );
}

export default forwardRef(Carousel);
